import { StaticImageData } from 'next/image'
import anfitrionBanner from 'public/images/help/help-center-anfitriones.jpg'
import faqsBanner from 'public/images/help/help-center-faqs.png'
import accountBanner from 'public/images/help/help-center-mi-cuenta.jpg'
import renterosBanner from 'public/images/help/help-center-renteros.jpg'
import securityBanner from 'public/images/help/help-center-seguridad-confianza.jpg'
import spotmeBanner from 'public/images/help/help-center-spotme.png'
import { IPost } from 'src/types/hygraph'
import { HygraphTopics } from 'src/utils/hygraph'

type ICategory = {
  title: string
  description?: string
  image?: StaticImageData
}

export const CATEGORIES: {
  [key in HygraphTopics]?: ICategory
} = {
  [HygraphTopics.HelpHost]: {
    title: 'Anfitriones',
    image: anfitrionBanner,
    description:
      'Encuentra aquí toda la información que necesites saber sobre ser Anfitrión.'
  },
  [HygraphTopics.HelpRent]: {
    title: 'Renteros',
    image: renterosBanner
  },
  [HygraphTopics.HelpFaq]: {
    title: 'FAQs',
    image: faqsBanner
  },
  [HygraphTopics.HelpAboutUs]: {
    title: 'SpotMe',
    image: spotmeBanner
  },
  [HygraphTopics.HelpSecurity]: {
    title: 'Seguridad y Confianza',
    image: securityBanner
  },
  [HygraphTopics.HelpMyAccount]: {
    title: 'Mi Cuenta',
    image: accountBanner
  }
}

/**
 * Hub articles pinned to the top of each category page (2026-09-05); everything
 * else keeps the `updatedAt desc` order. Without this the category pages were
 * ordered purely by last edit, so a bulk sync pushed hubs to the bottom.
 */
export const PINNED_HELP_SLUGS: Partial<Record<HygraphTopics, string[]>> = {
  [HygraphTopics.HelpHost]: [
    'anfitriones',
    'empieza-como-anfitrion',
    'pagos',
    'preguntas-frecuentes-del-anfitrion'
  ],
  [HygraphTopics.HelpRent]: [
    'renteros',
    'como-funciona-pago-mensual',
    'que-pasa-si-cancelo'
  ],
  [HygraphTopics.HelpFaq]: [
    'preguntas-frecuentes',
    'cancelacion',
    'factura-cfdi'
  ],
  [HygraphTopics.HelpAboutUs]: [
    'acerca-de-spotme-2',
    'los-tres-productos-spotme'
  ],
  [HygraphTopics.HelpSecurity]: ['seguridad-y-confianza-2'],
  [HygraphTopics.HelpMyAccount]: ['mi-cuenta']
}

/** Home-page "Lectura recomendada" — curated, not "most recently edited". */
export const FEATURED_HELP_SLUGS = [
  'los-tres-productos-spotme',
  'como-funciona-pago-mensual',
  'que-pasa-si-cancelo',
  'empieza-como-anfitrion',
  'pagos',
  'factura-cfdi'
]

const FEATURED_COUNT = 6

export const sortPinnedFirst = (posts: IPost[], pinned: string[] = []) => {
  const rank = new Map(pinned.map((slug, i) => [slug, i]))
  return [...posts].sort(
    (a, b) =>
      (rank.get(a.slug ?? '') ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(b.slug ?? '') ?? Number.MAX_SAFE_INTEGER)
  )
}

/** Curated featured list; backfills from `posts` order (updatedAt desc) if a curated slug is missing. */
export const pickFeaturedHelp = (posts: IPost[]) => {
  const bySlug = new Map(posts.map((p) => [p.slug, p]))
  const curated = FEATURED_HELP_SLUGS.map((slug) => bySlug.get(slug)).filter(
    (p): p is IPost => !!p
  )
  const rest = posts.filter((p) => !FEATURED_HELP_SLUGS.includes(p.slug ?? ''))
  return [...curated, ...rest].slice(0, FEATURED_COUNT)
}

/**
 * Category-page grouping by job-to-be-done (2026-09-05). A topic without an
 * entry renders one flat list; slugs not listed here fall into a trailing
 * "Más artículos" group, so a new article is never hidden. Order inside a group
 * follows the incoming (pinned-first) order.
 */
export type HelpGroupKey = 'start' | 'payments' | 'operate' | 'models' | 'other'

export const HELP_GROUPS: Partial<
  Record<HygraphTopics, { key: HelpGroupKey; slugs: string[] }[]>
> = {
  [HygraphTopics.HelpHost]: [
    {
      key: 'start',
      slugs: [
        'anfitriones',
        'empieza-como-anfitrion',
        'administracion-de-anuncios',
        'inventario-al-dia',
        'configura-tu-cuenta-de-pagos'
      ]
    },
    {
      key: 'payments',
      slugs: [
        'pagos',
        'cobro-directo',
        'deposito-en-garantia',
        'cierre-de-mes-para-aliados'
      ]
    },
    {
      key: 'operate',
      slugs: [
        'preguntas-frecuentes-del-anfitrion',
        'seguridad-para-el-anfitrion',
        'interesados-y-tratos-cerrados',
        'programa-de-referidos-spotme'
      ]
    },
    {
      key: 'models',
      slugs: [
        'renta-de-almacenamiento-personal',
        'renta-de-almacenamiento-para-vehiculos',
        'renta-de-almacenamiento-para-negocios',
        'flotillas-y-pensiones-grandes',
        'anfitrion-a-domicilio',
        'recibe-clientes-de-servicios-logisticos',
        'directorio-de-negocios'
      ]
    }
  ],
  [HygraphTopics.HelpRent]: [
    {
      key: 'start',
      slugs: [
        'renteros',
        'almacenamiento-personal',
        'almacenamiento-de-vehiculos',
        'almacenamiento-a-domicilio',
        'estacionamiento-para-flotillas',
        'verificacion-de-identidad-rentero'
      ]
    },
    {
      key: 'payments',
      slugs: [
        'como-funciona-pago-mensual',
        'metodos-de-pago-spei-tarjeta',
        'pagar-directo-al-anfitrion',
        'seguro-chubb-opcional'
      ]
    },
    { key: 'operate', slugs: ['que-pasa-si-cancelo'] }
  ],
  [HygraphTopics.HelpFaq]: [
    { key: 'start', slugs: ['preguntas-frecuentes'] },
    {
      key: 'payments',
      slugs: [
        'cancelacion',
        'politicas-de-reembolso',
        'factura-cfdi',
        'danos-y-reclamaciones',
        'disputas-y-profeco'
      ]
    },
    {
      key: 'models',
      slugs: [
        'que-es-spotme-pro',
        'que-es-spotme-tradicional',
        'que-es-spotme-tradicional-estacionamiento',
        'soluciones-logisticas-3pl'
      ]
    }
  ]
}

export type HelpGroup = { key: HelpGroupKey; posts: IPost[] }

export const groupHelpPosts = (
  topic: HygraphTopics,
  posts: IPost[]
): HelpGroup[] => {
  const groups = HELP_GROUPS[topic]
  if (!groups) return [{ key: 'other', posts }]
  const placed = new Set<string>()
  const out: HelpGroup[] = []
  for (const group of groups) {
    const members = posts.filter((p) => group.slugs.includes(p.slug))
    members.forEach((p) => placed.add(p.slug))
    if (members.length) out.push({ key: group.key, posts: members })
  }
  const rest = posts.filter((p) => !placed.has(p.slug))
  if (rest.length) out.push({ key: 'other', posts: rest })
  return out
}
